import React, { useState, useEffect } from 'react';
import { ringoverStatsAPI } from '../../services/api';
import { Clock, Users, Phone, TrendingUp, Calendar, AlertCircle } from 'lucide-react';

const RingoverStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateStart, setDateStart] = useState(new Date().toISOString().split('T')[0]);
  const [dateEnd, setDateEnd] = useState(new Date().toISOString().split('T')[0]);
  const [totalStats, setTotalStats] = useState({});
  const [sortField, setSortField] = useState('total_idle_time_seconds');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedAgents, setExpandedAgents] = useState({});

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Appel API avec params:', { date_start: dateStart, date_end: dateEnd });
      
      const response = await ringoverStatsAPI.getTimeBetweenCalls({
        date_start: dateStart,
        date_end: dateEnd
      });
      
      console.log('Réponse API reçue:', response.data);
      
      if (response.data.success) {
        setStats(response.data.data || []);
        setTotalStats(response.data);
        console.log('Données chargées:', response.data.data?.length, 'agents');
      } else {
        setError('Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error('Erreur API complète:', err);
      console.error('Erreur response:', err.response);
      
      if (err.code === 'ECONNABORTED') {
        setError('La requête a expiré. Les données sont peut-être en cours de calcul. Réessayez dans quelques instants.');
      } else if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (err.response?.status === 500) {
        setError('Erreur serveur: ' + (err.response?.data?.message || 'Erreur interne'));
      } else if (!err.response) {
        setError('Impossible de contacter le serveur. Vérifiez que l\'API est démarrée.');
      } else {
        setError('Erreur: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAgentDetails = (agentId) => {
    setExpandedAgents((prev) => ({
      ...prev,
      [agentId]: !prev[agentId],
    }));
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchStats();
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const sortedStats = [...stats].sort((a, b) => {
    const aValue = a[sortField] || 0;
    const bValue = b[sortField] || 0;
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  // Calculer les totaux globaux
  const globalTotals = stats.reduce((acc, agent) => {
    acc.totalIdleTime += agent.total_idle_time_seconds || 0;
    acc.totalCalls += agent.total_calls || 0;
    acc.totalAgents += 1;
    acc.totalCallDuration += agent.total_call_duration_seconds || 0;
    return acc;
  }, { totalIdleTime: 0, totalCalls: 0, totalAgents: 0, totalCallDuration: 0 });

  const averageIdleTime = globalTotals.totalAgents > 0 ? globalTotals.totalIdleTime / globalTotals.totalAgents : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Statistiques Temps Entre Appels
        </h1>
        <p className="text-gray-600">
          Analyse des temps d'inactivité entre les appels par agent depuis Ringover
        </p>
      </div>

      {/* Formulaire de filtres */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
          >
            <Calendar className="h-4 w-4" />
            <span>{loading ? 'Chargement...' : 'Actualiser'}</span>
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-bold text-gray-900">{globalTotals.totalAgents}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Phone className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Appels</p>
              <p className="text-2xl font-bold text-gray-900">{globalTotals.totalCalls}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Temps Entre Appels</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatDuration(globalTotals.totalIdleTime)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Temps Moyen Entre Appels</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatDuration(averageIdleTime)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Avertissement si limite atteinte */}
      {totalStats.is_limited && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5" />
          <span>{totalStats.limit_reached}</span>
        </div>
      )}

      {/* Tableau des agents */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Temps Entre Appels par Agent
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Période: {dateStart} au {dateEnd} • {stats.length} agents
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th 
                  onClick={() => handleSort('total_calls')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Appels</span>
                    {getSortIcon('total_calls')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('answered_calls')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Appels Répondus</span>
                    {getSortIcon('answered_calls')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('total_idle_time_seconds')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Temps Entre Appels</span>
                    {getSortIcon('total_idle_time_seconds')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('average_idle_time_seconds')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Temps Moyen Entre Appels</span>
                    {getSortIcon('average_idle_time_seconds')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('max_idle_time_seconds')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Temps Max</span>
                    {getSortIcon('max_idle_time_seconds')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('idle_intervals_count')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Intervalles</span>
                    {getSortIcon('idle_intervals_count')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('average_hierarchy_effort_percentage')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Moyenne Effort Hiérarchique</span>
                    {getSortIcon('average_hierarchy_effort_percentage')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span>Chargement des statistiques...</span>
                    </div>
                  </td>
                </tr>
              ) : sortedStats.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    Aucune donnée disponible pour cette période
                  </td>
                </tr>
              ) : (
                sortedStats.map((agent) => (
                  <React.Fragment key={agent.agent_id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                            {agent.agent_name ? agent.agent_name.charAt(0).toUpperCase() : 'A'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {agent.agent_name || 'Agent inconnu'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {agent.agent_id}
                          </div>
                          {agent.agent_email && (
                            <div className="text-xs text-gray-400">
                              {agent.agent_email}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleAgentDetails(agent.agent_id)}
                        className="mt-3 inline-flex items-center px-3 py-1 rounded-md text-sm font-medium border border-gray-300 hover:border-blue-500 hover:text-blue-600 transition-colors"
                      >
                        {expandedAgents[agent.agent_id] ? '− Masquer l’historique' : '+ Voir l’historique'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-medium">{agent.total_calls}</span>
                        <div className="text-xs text-gray-500">
                          <span className="text-green-600">↗ {agent.outbound_calls}</span>
                          <span className="mx-1">•</span>
                          <span className="text-blue-600">↙ {agent.inbound_calls}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        agent.answered_calls / agent.total_calls >= 0.8 ? 'bg-green-100 text-green-800' :
                        agent.answered_calls / agent.total_calls >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {agent.answered_calls} ({Math.round((agent.answered_calls / agent.total_calls) * 100)}%)
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-medium">{formatDuration(agent.total_idle_time_seconds)}</span>
                        <span className="text-xs text-gray-500">
                          {Math.round(agent.total_idle_time_minutes)} minutes
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        agent.average_idle_time_seconds <= 60 ? 'bg-green-100 text-green-800' :
                        agent.average_idle_time_seconds <= 300 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {formatDuration(agent.average_idle_time_seconds)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="text-red-600 font-medium">
                        {formatDuration(agent.max_idle_time_seconds)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {agent.idle_intervals_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {agent.average_hierarchy_effort_percentage !== null && agent.average_hierarchy_effort_percentage !== undefined ? (
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[80px]">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                agent.average_hierarchy_effort_percentage >= 80 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                                agent.average_hierarchy_effort_percentage >= 60 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                                'bg-gradient-to-r from-red-400 to-red-600'
                              }`}
                              style={{ width: `${Math.min(agent.average_hierarchy_effort_percentage, 100)}%` }}
                            ></div>
                          </div>
                          <span className={`text-xs font-semibold min-w-[35px] ${
                            agent.average_hierarchy_effort_percentage >= 80 ? 'text-green-700' :
                            agent.average_hierarchy_effort_percentage >= 60 ? 'text-yellow-700' :
                            'text-red-700'
                          }`}>
                            {agent.average_hierarchy_effort_percentage.toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                  </tr>
                  {expandedAgents[agent.agent_id] && (
                    <tr>
                      <td colSpan="8" className="bg-gray-50 px-6 py-4">
                        <div className="space-y-4">
                          <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                            <div>
                              <p className="font-semibold text-gray-900">Total jours analysés</p>
                              <p>{agent.total_days || (agent.history ? agent.history.length : 0)}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Durée totale d’activité</p>
                              <p>{formatDuration(agent.activity_duration_seconds)}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Durée totale d’appel</p>
                              <p>{formatDuration(agent.total_call_duration_seconds)}</p>
                            </div>
                          </div>
                          {agent.history && agent.history.length > 0 ? (
                            <div className="space-y-4">
                              {agent.history.map((day) => (
                                <div key={`${agent.agent_id}-${day.date}`} className="border border-gray-200 rounded-lg overflow-hidden">
                                  <div className="bg-gray-100 px-4 py-3 flex justify-between items-center">
                                    <div className="flex gap-6">
                                      <div>
                                        <span className="text-xs font-semibold text-gray-600 uppercase">Date</span>
                                        <p className="text-sm font-bold text-gray-900">{day.date}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-600 uppercase">Temps total</span>
                                        <p className="text-sm text-gray-900">{formatDuration(day.total_idle_time_seconds)}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-600 uppercase">Temps moyen</span>
                                        <p className="text-sm text-gray-900">{formatDuration(day.average_idle_time_seconds)}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-600 uppercase">Intervalles</span>
                                        <p className="text-sm text-gray-900">{day.idle_intervals_count}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-600 uppercase">Appels</span>
                                        <p className="text-sm text-gray-900">{day.total_calls}</p>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {day.idle_intervals_details && day.idle_intervals_details.length > 0 && (
                                    <div className="bg-white p-4">
                                      <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Détails des intervalles entre appels</h4>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                          <thead className="bg-gray-50">
                                            <tr>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Appel précédent</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fin</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Appel suivant</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Début</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Temps d'inactivité</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100">
                                            {day.idle_intervals_details.map((interval, idx) => (
                                              <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-xs text-gray-500">{idx + 1}</td>
                                                <td className="px-3 py-2 text-xs font-mono text-gray-700">{interval.from_call}</td>
                                                <td className="px-3 py-2 text-xs text-gray-600">{new Date(interval.from_end).toLocaleTimeString('fr-FR')}</td>
                                                <td className="px-3 py-2 text-xs font-mono text-gray-700">{interval.to_call}</td>
                                                <td className="px-3 py-2 text-xs text-gray-600">{new Date(interval.to_start).toLocaleTimeString('fr-FR')}</td>
                                                <td className="px-3 py-2">
                                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    interval.idle_seconds <= 30 ? 'bg-green-100 text-green-800' :
                                                    interval.idle_seconds <= 120 ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                  }`}>
                                                    {formatDuration(interval.idle_seconds)}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              Aucun historique disponible pour cette période.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Informations supplémentaires */}
      {totalStats.period && (
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Période analysée:</span> {totalStats.period.date_start} au {totalStats.period.date_end}
            </div>
            <div>
              <span className="font-medium">Total appels traités:</span> {totalStats.total_calls}
            </div>
            <div>
              <span className="font-medium">Généré le:</span> {new Date(totalStats.generated_at).toLocaleString('fr-FR')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RingoverStats;
